import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// The workshop light as the movement is first opened (a low, ruddy lamp),
// half assembled, and running under a bright, warm lamp.
const STOPS = {
  plateTop: ['#1A120B', '#1D140C', '#21160D'],
  lamp: ['#A85E2E', '#D2853F', '#F6A956'],
  lampCore: ['#E6A066', '#F7BE7C', '#FFD89C'],
} as const;

type ColorName = keyof typeof STOPS;

export type ClockworkColors = Record<ColorName, Rgb>;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = NAMES.reduce(
  (all, name) => ({ ...all, [name]: STOPS[name].map(hexToRgb) }),
  {} as Record<ColorName, Rgb[]>,
);

/** The light on the movement as it is assembled (0 when first opened, 1 when it runs). */
export const clockworkColors = (assembly: number): ClockworkColors => {
  const scaled = clamp(assembly) * 2;
  const index = Math.min(1, Math.floor(scaled));
  const amount = smoothstep(0, 1, scaled - index);
  return NAMES.reduce(
    (all, name) => ({ ...all, [name]: mixRgb(PARSED[name][index], PARSED[name][index + 1], amount) }),
    {} as ClockworkColors,
  );
};

/** The engraved plate stays dark brass behind the header, so the header text stays white. */
export const clockworkChromeTone = (progress: number): ChromeTone =>
  luminance(clockworkColors(progress).plateTop) > 0.31 ? 'dark' : 'light';
